import type { OrganizationCreationFailedAction } from '@Application/Organizations/Organizations/Actions/organizationCreationFailed';
import type { OrganizationUpdateFailedAction } from '@Application/Organizations/Organizations/Actions/organizationUpdateFailed';
import CreateOrganization from '@Application/Organizations/Organizations/Commands/CreateOrganization';
import UpdateOrganization from '@Application/Organizations/Organizations/Commands/UpdateOrganization';
import createOrganization from '@Application/Organizations/Organizations/createOrganization';
import updateOrganization from '@Application/Organizations/Organizations/updateOrganization';
import organizationSidebarSignal, {
	closeOrganizationSidebar,
} from '@UI/Pages/Administration/Organizations/Signals/organizationSidebarSignal';
import OrganizationFormData from '@UI/Pages/Administration/Organizations/Sidebars/OrganizationSidebar/FormData/OrganizationFormData';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, InputText, Sidebar, TextArea } from '@verclam/ui-kit';

/**
 * One panel for both creation and edition, told apart by `organizationId` on the signal's context.
 * Reads the edited organization straight from the store rather than through a prop: the page mounts
 * this component once, with no data of its own to pass it.
 */
const OrganizationSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(organizationSidebarSignal);
	const organizations = useSelector((state) => state.organizationsStore.organizations);
	const { user } = useStrictAuthenticatedUser();

	const [formData, setFormData] = React.useState(() => OrganizationFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const organizationId = context?.organizationId ?? null;

	const editedOrganization = React.useMemo(() => {
		if (organizationId === null || !organizations) {
			return null;
		}

		return organizations.findById(organizationId);
	}, [organizationId, organizations]);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		setFormData(
			editedOrganization !== null
				? OrganizationFormData.fromView(editedOrganization)
				: OrganizationFormData.createEmpty(),
		);
	}, [context, editedOrganization]);

	const handleNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setName(value));
	}, []);

	const handleSiretChange = React.useCallback((value: string) => {
		setFormData((current) => current.setSiret(value));
	}, []);

	const handleHeadOfficeAddressChange = React.useCallback((value: string) => {
		setFormData((current) => current.setHeadOfficeAddress(value));
	}, []);

	const handleDocumentFooterMentionsChange = React.useCallback((value: string) => {
		setFormData((current) => current.setDocumentFooterMentions(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppShell` never mounts this page without an account.
		if (user.account === null) {
			return;
		}

		const accountId = user.account.id;
		const siret = formData.siret === '' ? null : formData.siret;
		const headOfficeAddress = formData.headOfficeAddress === '' ? null : formData.headOfficeAddress;
		const documentFooterMentions =
			formData.documentFooterMentions === '' ? null : formData.documentFooterMentions;

		setIsSubmitting(true);

		if (organizationId === null) {
			dispatch(
				createOrganization(
					new CreateOrganization(
						accountId,
						formData.name,
						siret,
						headOfficeAddress,
						documentFooterMentions,
					),
				),
			)
				.then(() => closeOrganizationSidebar())
				.catch((action: OrganizationCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'ORGANIZATION_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateOrganization(
				new UpdateOrganization(
					accountId,
					organizationId,
					formData.name,
					siret,
					headOfficeAddress,
					documentFooterMentions,
				),
			),
		)
			.then(() => closeOrganizationSidebar())
			.catch((action: OrganizationUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'ORGANIZATION_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, organizationId, user, dispatch]);

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeOrganizationSidebar}
			position="right"
			header={organizationId === null ? 'Nouvelle structure' : 'Modifier la structure'}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						label="Annuler"
						onClick={closeOrganizationSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form
				id={formId}
				onSubmit={handleSubmit}
				aria-label={organizationId === null ? 'Créer une structure' : 'Modifier une structure'}
			>
				<InputText
					name="name"
					label="Raison sociale"
					required
					value={formData.name}
					onChange={handleNameChange}
					errorMessage={formData.getValidationMessage('name')}
					isFormFieldValid={formData.isValid('name')}
				/>
				<InputText
					name="siret"
					label="SIRET"
					value={formData.siret ?? ''}
					onChange={handleSiretChange}
					errorMessage={formData.getValidationMessage('siret')}
					isFormFieldValid={formData.isValid('siret')}
				/>
				<InputText
					name="headOfficeAddress"
					label="Adresse du siège"
					value={formData.headOfficeAddress ?? ''}
					onChange={handleHeadOfficeAddressChange}
					errorMessage={formData.getValidationMessage('headOfficeAddress')}
					isFormFieldValid={formData.isValid('headOfficeAddress')}
				/>
				<TextArea
					name="documentFooterMentions"
					label="Mentions de pied de document"
					rows={4}
					value={formData.documentFooterMentions ?? ''}
					onChange={handleDocumentFooterMentionsChange}
					errorMessage={formData.getValidationMessage('documentFooterMentions')}
					isFormFieldValid={formData.isValid('documentFooterMentions')}
				/>
			</Form>
		</Sidebar>
	);
};

export default OrganizationSidebar;
